import { isDefined } from "@prismicio/editor-support/Type"
import type { TextBlock } from "@prismicio/types-internal/lib/content"
import { RichTextNodeType } from "@prismicio/types-internal/lib/customtypes"
import { mergeAttributes } from "@tiptap/core"
import TTOrderedList from "@tiptap/extension-ordered-list"
import type { Node as ProsemirrorNode, Schema } from "prosemirror-model"

import { ListItem } from "../../coreExtensions"
import { converter as TextDirection } from "../../globalExtensions/TextDirection"
import { ListExtension, NodeUtils } from "../../models"

const component = TTOrderedList.extend({
  name: "orderedList",
  addAttributes() {
    return {
      start: {
        default: 1,
        parseHTML: (element) => {
          return element.hasAttribute("start")
            ? parseInt(element.getAttribute("start") || "", 10)
            : 1
        },
      },
    }
  },

  renderHTML({ HTMLAttributes }) {
    const { start, ...attributesWithoutStart } = HTMLAttributes

    return start === 1
      ? [
          "ol",
          mergeAttributes(this.options.HTMLAttributes, attributesWithoutStart),
          0,
        ]
      : ["ol", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0]
  },

  addCommands() {
    return {
      toggleOrderedList:
        () =>
        ({ commands }) => {
          return commands.toggleList(this.name, this.options.itemTypeName)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      "Mod-Shift-7": () => this.editor.commands.toggleOrderedList(),
    }
  },
}).configure({
  itemTypeName: "listItem",
})

const converter = {
  fromPrismic(
    schema: Schema,
  ): (orderedList: TextBlock) => ProsemirrorNode | undefined {
    return function (orderedList: TextBlock): ProsemirrorNode | undefined {
      const content = ListItem.converter.fromPrismic(schema)(orderedList)
      if (!content) return

      return schema.nodes[component.name]?.create(
        TextDirection.fromPrismic(orderedList),
        content,
      )
    }
  },

  toPrismic(node: ProsemirrorNode): readonly TextBlock[] {
    return NodeUtils.children
      .map(node, (child: ProsemirrorNode) => {
        return ListItem.converter
          .toPrismic(child, RichTextNodeType.orderedList)
          .filter(isDefined)
      })
      .flat()
  },
}
const EditorOrderedList = new ListExtension(
  [RichTextNodeType.orderedList],
  component.name,
  component,
  converter,
)

// eslint-disable-next-line import/no-default-export
export default EditorOrderedList
